<script lang="ts" setup>
  import { useElementSize } from '@vueuse/core';
  import { ElPagination, ElTable, ElTableColumn } from 'element-plus';
  import { computed, ref, useSlots, watch } from 'vue';
  import { excludeObjectKeys } from '../../../utils';
  import { TableProps } from './table';

  defineOptions({
    name: 'ITable',
  });

  const props = defineProps(TableProps);
  const emits = defineEmits([
    'update:pagination',
    'sizeChange',
    'currentChange',
    'select',
    'selectAll',
    'selectionChange',
    'cellMouseEnter',
    'cellMouseLeave',
    'cellClick',
    'cellDblclick',
    'cellContextmenu',
    'rowClick',
    'rowContextmenu',
    'rowDblclick',
    'headerClick',
    'headerContextmenu',
    'sortChange',
    'filterChange',
    'currentChange',
    'headerDragend',
    'expandChange',
  ]);
  const tableAttrs = computed(() => excludeObjectKeys(props, ['height', 'columns', 'pagination', 'loading']));

  const tableWarpRef = ref();
  const slots = useSlots();

  // @ts-ignore
  const { height } = useElementSize(tableWarpRef);
  const tableHeight = computed(() => {
    if (props.height) {
      return props.height;
    } else {
      if (props.pagination?.currentPage) {
        return height.value - 50;
      }
      return height.value;
    }
  });

  const pagina = ref(props.pagination);
  watch(
    () => props.pagination,
    (val: any) => {
      pagina.value = {
        ...props.pagination,
        ...val,
      };
    },
    { deep: true },
  );

  function handleSizeChange(pageSize: number) {
    emits('update:pagination', {
      ...props.pagination,
      pageSize,
    });
    emits('sizeChange', pageSize);
  }

  function handleCurrentChange(currentPage: number) {
    emits('update:pagination', {
      ...props.pagination,
      currentPage,
    });
    emits('currentChange', currentPage);
  }

  const tableRef = ref();
  defineExpose({
    tableRef() {
      return tableRef.value;
    },
  });
</script>

<template>
  <div ref="tableWarpRef" v-loading="loading" class="i-table-warp">
    <ElTable
      ref="tableRef"
      v-bind="tableAttrs"
      :height="tableHeight"
      class="i-table"
      @select="(selection, row) => emits('select', selection, row)"
      @select-all="selection => emits('selectAll', selection)"
      @selection-change="newSelection => emits('selectionChange', newSelection)"
      @cell-mouse-enter="(row, column, cell, event) => emits('cellMouseEnter', row, column, cell, event)"
      @cell-mouse-leave="(row, column, cell, event) => emits('cellMouseLeave', row, column, cell, event)"
      @cell-click="(row, column, cell, event) => emits('cellClick', row, column, cell, event)"
      @cell-dblclick="(row, column, cell, event) => emits('cellDblclick', row, column, cell, event)"
      @cell-contextmenu="(row, column, cell, event) => emits('cellContextmenu', row, column, cell, event)"
      @row-click="(row, column, event) => emits('rowClick', row, column, event)"
      @row-contextmenu="(row, column, event) => emits('rowContextmenu', row, column, event)"
      @row-dblclick="(row, column, event) => emits('rowDblclick', row, column, event)"
      @header-click="(column, event) => emits('headerClick', column, event)"
      @header-contextmenu="(column, event) => emits('headerContextmenu', column, event)"
      @sort-change="(column, prop, order) => emits('sortChange', column, prop, order)"
      @filter-change="filters => emits('filterChange', filters)"
      @header-dragend="(newWidth, oldWidth, column, event) => emits('headerDragend', newWidth, oldWidth, column, event)"
      @expand-change="(row, expandedRows) => emits('expandChange', row, expandedRows)"
    >
      <template v-for="column of columns" :key="column.prop">
        <!-- todo 使用v-bind会报typescript类型错误，所以只能一个个的写上 -->
        <ElTableColumn
          :type="column.type"
          :index="column.index"
          :label="column.label"
          :prop="column.prop"
          :row-key="column.rowKey || 'id'"
          :column-key="column.columnKey"
          :width="column.width"
          :min-width="column.minWidth"
          :fixed="column.fixed"
          :sortable="column.sortable"
          :sort-method="column.sortMethod"
          :sort-by="column.sortBy"
          :sort-orders="column.sortOrders"
          :resizable="column.resizable"
          :show-overflow-tooltip="column.showOverflowTooltip"
          :align="column.align"
          :header-align="column.headerAlign"
          :class-name="column.className"
          :label-class-name="column.labelClassName"
          :selectable="column.selectable"
          :reserve-selection="column.reserveSelection"
          :filters="column.filters"
          :filter-placement="column.filterPlacement"
          :filter-multiple="column.filterMultiple"
          :filter-method="column.filterMethod"
          :filtered-value="column.filteredValue"
        >
          <template #default="scope">
            <!-- eslint-disable-next-line -->
            <slot v-if="column.slot" :name="column.slot" :row="scope.row" :$index="scope.$index" />
          </template>
          <template v-if="slots[`${column.prop}TableHeader`]" #header>
            <slot :name="`${column.prop}TableHeader`" />
          </template>
        </ElTableColumn>
      </template>
    </ElTable>
    <div v-if="pagina.total" class="i-table-pagination">
      <ElPagination
        v-model:current-page="pagina.currentPage"
        v-model:page-size="pagina.pageSize"
        :small="pagina.small || false"
        :disabled="pagina.disabled || false"
        :background="pagina.background || true"
        :total="pagina.total || 0"
        :pager-count="pagina.pagerCount || 7"
        :layout="pagina.layout || 'total, sizes, prev, pager, next, jumper'"
        :page-sizes="pagina.pageSizes || [20, 30, 50, 100]"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>
